import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import { CheckCircle, Loader, AlertCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No session ID found');
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await api.get(`/api/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else if (response.data.status === 'expired') {
          setStatus('error');
          setError('Payment session expired');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, pollInterval);
        } else {
          setStatus('error');
          setError('Payment verification timeout. Please contact support.');
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Error verifying payment. Please contact support.';
        setStatus('error');
        setError(errorMessage);
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-page)' }}>
      <div className="max-w-md w-full p-8 rounded-lg text-center" style={{ background: 'var(--bg-card)' }}>
        {status === 'checking' && (
          <>
            <Loader size={64} className="mx-auto mb-4 animate-spin" style={{ color: 'var(--accent-blue-400)' }} />
            <h1 className="heading-2 mb-4">Processing Payment...</h1>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={64} className="mx-auto mb-4" style={{ color: 'var(--accent-green-200)' }} />
            <h1 className="heading-2 mb-4">Payment Successful!</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Thank you for your purchase. Your account has been created.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
              Redirecting to login...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={64} className="mx-auto mb-4" style={{ color: '#DC2626' }} />
            <h1 className="heading-2 mb-4">Payment Issue</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              {error}
            </p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Return Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;